import React from 'react';
import JSONbig from "json-bigint";
import {Job, JobTypeDescription} from "../../models/job";
import {NotificationService} from "../../common/notification";
import {jobsListApi, jobsRescheduleApi} from "./jobsApi";
import Stack from "@mui/joy/Stack";
import Sheet from "@mui/joy/Sheet";
import Tooltip from "@mui/joy/Tooltip";
import DebounceInput from "../../components/debounceInput/DebounceInput";
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import IconButton, {iconButtonClasses} from "@mui/joy/IconButton";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import CircularProgress from '@mui/joy/CircularProgress';
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import {useNavigate} from "react-router-dom";
import {getPages} from "../../common/pages/pages";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import { formatDateTime } from "../../common/dateTime";
import ConfirmDialog from "../../components/confirmDialog/ConfirmDialog";
import Backdrop from '@mui/material/Backdrop';

const JSONbigNative = JSONbig({ useNativeBigInt: true });

type DefaultProps = {
    userId?: bigint
}

export function JobsTable(props: DefaultProps) {
    const {userId} = props;
    const navigate = useNavigate();
    const [backdrop, setBackdrop] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(20);
    const [steamIdFilter, setSteamIdFilter] = React.useState<bigint>(userId || BigInt(-1));
    const [jobTypeFilter, setJobTypeFilter] = React.useState<number>(-1);
    const [failureFilter, setFailureFilter] = React.useState<number>(-1);
    const [selectedJobId, setSelectedJobId] = React.useState<string>('');
    const [jobs, setJobs] = React.useState<Job[]>([]);
    const [isLoading, setLoading] = React.useState(false);
    const [rescheduleConfirmOpen, setRescheduleConfirmOpen] = React.useState<boolean>(false);
    const [filtersOpen, setFiltersOpen] = React.useState(false);

    React.useEffect(() => {
        if (!filtersOpen) {
            loadData().catch(console.error);
        }
    }, [page, steamIdFilter, jobTypeFilter, failureFilter]);

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await jobsListApi(page, 20, steamIdFilter, failureFilter, jobTypeFilter);
            if (result.result) {
                const res = result.value!;
                setPageCount(Math.ceil(res.count / 20));
                setJobs(res.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const applyFilter = async () => {
        setFiltersOpen(false)
        if (page !== 1) {
            setPage(1);
        }
    }

    const renderFilters = () => {
        return (
            <React.Fragment>
                <FormControl size="sm">
                    <FormLabel>Тип</FormLabel>
                    <Select
                        size="sm"
                        placeholder="Тип задачи"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={jobTypeFilter}
                        onChange={(_, value) => setJobTypeFilter(value ?? -1)}
                    >
                        <Option value={-1}>Все</Option>
                        {
                            Object.entries(JobTypeDescription).map((entity) => <Option key={Number(entity[0])}
                                value={Number(entity[0])}>{entity[1]}</Option>)
                        }
                    </Select>
                </FormControl>
                {userId !== undefined
                ? <></>
                : <FormControl size="sm">
                    <FormLabel>Пользователь (Steam ID)</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Пользователь"
                        defaultValue={steamIdFilter > -1 ? steamIdFilter.toString() : ''}
                        debounceTimeout={500}
                        handleDebounce={(val) => setSteamIdFilter(JSONbigNative.parse(val || '-1'))}
                    />
                </FormControl>}
                <FormControl size="sm">
                    <FormLabel>Ошибки</FormLabel>
                    <Select size="sm"
                            placeholder="Ошибки"
                            value={failureFilter}
                            onChange={(_, value) => setFailureFilter(value ?? -1)}>
                        <Option value={-1}>Все</Option>
                        <Option value={0}>Без ошибок</Option>
                        <Option value={1}>С ошибками</Option>                        
                    </Select>
                </FormControl>
            </React.Fragment>
        );
    }

    const reschedule = (jobId: string) => {
        setSelectedJobId(jobId);
        setRescheduleConfirmOpen(true);
    }

    const onReschedule = async () => {
        if (selectedJobId === '') {
            return;
        }

        setBackdrop(true)
        try {
            let result = await jobsRescheduleApi(selectedJobId);
            if (result.result) {
                NotificationService.alertSuccess("Повторная обработка задачи была запущена.")
            } else {
                NotificationService.alertError("Не удалось запустить повторную обработку задачи. Пожалуйста попробуйте позже или обратитесь в службу поддержки.")
            }
        } finally {
            setSelectedJobId('');
            setBackdrop(false)
        }
    }

    return (
        <React.Fragment>
            <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={backdrop}
                onClick={() => setBackdrop(false)}
            >
                <CircularProgress />
            </Backdrop>

            <ConfirmDialog
                title="Ручной запуск."
                open={rescheduleConfirmOpen}
                setOpen={setRescheduleConfirmOpen}
                onConfirm={onReschedule}
            >
                Будет произведена повторная обработка задачи. Продолжить?
            </ConfirmDialog>

            {isLoading ?
                <CircularProgress/>
                :
                <>
                    <Sheet
                        className="SearchAndFilters-mobile"
                        sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
                    >
                        <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => setFiltersOpen(true)}
                        >
                            <FilterAltIcon/>
                        </IconButton>

                        <Modal open={filtersOpen} onClose={() => setFiltersOpen(false)}>
                            <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
                                <ModalClose/>
                                <Typography id="filter-modal" level="h2">
                                    Фильтры
                                </Typography>
                                <Divider sx={{my: 2}}/>
                                <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                    {renderFilters()}
                                    <Button color="primary" onClick={applyFilter}>
                                        Применить
                                    </Button>
                                    <Button color="neutral" onClick={() => setFiltersOpen(false)}>
                                        Закрыть
                                    </Button>
                                </Sheet>
                            </ModalDialog>
                        </Modal>
                    </Sheet>
                    <Box
                        className="SearchAndFilters-tabletUp"
                        sx={{
                            borderRadius: 'sm',
                            py: 2,
                            display: {xs: 'none', sm: 'flex'},
                            flexWrap: 'wrap',
                            gap: 1.5,
                            '& > *': {
                                minWidth: {xs: '120px', md: '160px'},
                            },
                        }}
                    >
                        {renderFilters()}
                    </Box>
                    <Sheet
                        className="OrderTableContainer"
                        sx={{
                            display: {xs: 'none', sm: 'initial'},
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            minHeight: 0,
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{width: 100, padding: '12px 6px'}}>ID</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Hangfire Id</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Тип</th>
                                <th style={{width: 150, padding: '12px 6px'}}>Параметр</th>
                                <th style={{width: 150, padding: '12px 6px'}}>Пользователь</th>
                                <th style={{width: 180, padding: '12px 6px'}}>Создано</th>
                                <th style={{width: 250, padding: '12px 6px'}}>Ошибка</th>
                                <th style={{width: 70, padding: '12px 6px'}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {jobs.map((row) => (
                                <tr key={row.id} style={row.failedAt !== undefined
                                ? {backgroundColor: 'red'}
                                : {}}>
                                    <td>
                                        <Typography level="body-xs">#{row.id}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">#{row.jobId}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{JobTypeDescription[row.jobType]}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs" style={{ wordWrap: "break-word" }}>{row.jobParams}</Typography>
                                    </td>
                                    <td>
                                        {row.steamId === undefined
                                        ? <></>
                                        : <Stack spacing={0.5} direction={'column'}>
                                            <Link
                                                underline="hover"
                                                onClick={() => navigate(`/app/users/${row.steamId!.toString()}`)}
                                                level="body-xs"
                                            >
                                                {(row.userName || row.steamId).toString()}
                                            </Link>
                                            <Link
                                                underline="hover"
                                                href={`https://steamcommunity.com/profiles/${row.steamId.toString()}/`}
                                                target="_blank"
                                                level="body-xs"
                                            >
                                                (Steam link)
                                            </Link>
                                        </Stack>}
                                    </td>
                                    <td>
                                        <Stack spacing={0.5} direction={'column'}>
                                            <Typography level="body-xs">{formatDateTime(row.created)}</Typography>
                                            {row.comment
                                            ? <Typography level="body-xs">{row.comment}</Typography>
                                            : <></>}
                                        </Stack>
                                    </td>
                                    <td>
                                        {row.failedAt === undefined
                                        ? <></>
                                        : <Stack spacing={0.5} direction={'column'}>
                                            <Typography level="body-xs">
                                                {formatDateTime(row.failedAt)}
                                            </Typography>
                                            <Typography level="body-xs">{row.errorMessage}</Typography>
                                        </Stack>}
                                    </td>
                                    <td>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                            {row.failedAt === undefined
                                            ? <></>
                                            : <Tooltip
                                                placement="top-end"
                                                variant="outlined"
                                                arrow
                                                title="Запустить повторную обработку задачи">
                                                <IconButton
                                                    size="sm"
                                                    variant="outlined"
                                                    aria-label="Повторная обработка"
                                                    onClick={() => reschedule(row.jobId)}
                                                >
                                                    <RocketLaunchIcon/>
                                                </IconButton>
                                            </Tooltip>}
                                        </Box>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Sheet>
                    <Box
                        className="Pagination-laptopUp"
                        sx={{
                            pt: 2,
                            gap: 1,
                            [`& .${iconButtonClasses.root}`]: {borderRadius: '50%'},
                            display: {
                                xs: 'none',
                                md: 'flex',
                            },
                        }}
                    >
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<KeyboardArrowLeftIcon/>}
                            disabled={page === 1}
                            onClick={() => setPage(state => state - 1)}
                        >
                            Назад
                        </Button>

                        <Box sx={{flex: 1}}/>
                        {getPages(pageCount).map((p) => (
                            <IconButton
                                key={p}
                                size="sm"
                                variant={Number(p) ? 'outlined' : 'plain'}
                                color={page === Number(p) ? "primary" : "neutral"}
                                onClick={() => setPage(Number(p))}
                            >
                                {p}
                            </IconButton>
                        ))}
                        <Box sx={{flex: 1}}/>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            endDecorator={<KeyboardArrowRightIcon/>}
                            disabled={page === pageCount}
                            onClick={() => setPage(state => state + 1)}
                        >
                            Дальше
                        </Button>
                    </Box>
                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {jobs.map((listItem) => (
                            <List key={listItem.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                    style={listItem.failedAt !== undefined
                                        ? {backgroundColor: 'red'}
                                        : {}}
                                >
                                    <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <div>
                                            <Typography level="body-xs">ID: {listItem.id} (HF ID {listItem.jobId})</Typography>
                                            <Typography level="body-xs">Тип: {JobTypeDescription[listItem.jobType]}</Typography>
                                            <Typography level="body-xs">Параметр: {listItem.jobParams}</Typography>
                                            <Typography gutterBottom level="body-xs">
                                                Создано: {formatDateTime(listItem.created)}
                                            </Typography>
                                            {listItem.comment ? <Typography level="body-xs">Комментарий: {listItem.comment}</Typography> : <></>}
                                            {listItem.steamId === undefined
                                            ? <></>
                                            : <Stack spacing={1}
                                                direction={'row'}
                                                sx={{
                                                        justifyContent: "left",
                                                        alignItems: "center"
                                                    }}
                                            >
                                                <Typography level="body-xs">Пользователь:</Typography>
                                                <Link
                                                    underline="hover"
                                                    onClick={() => navigate(`/app/users/${listItem.steamId!.toString()}`)}
                                                    level="body-xs"
                                                    fontSize={14}
                                                >
                                                    {(listItem.userName || listItem.steamId).toString()}
                                                </Link>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/profiles/${listItem.steamId.toString()}/`}
                                                    target="_blank"
                                                    level="body-xs"
                                                    style={{
                                                        minWidth: 80,
                                                        fontSize: 14
                                                    }}
                                                >
                                                    (Steam link)
                                                </Link>
                                            </Stack>}
                                            {listItem.failedAt === undefined
                                            ? <></>
                                            : <>
                                                <Typography level="body-xs">
                                                    Ошибка от {formatDateTime(listItem.failedAt)}
                                                </Typography>
                                                {listItem.errorMessage
                                                ? <Typography level="body-xs" textColor={'danger.darkChannel'}>Доп.Информация: {listItem.errorMessage}</Typography>
                                                : <></>}
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, ml: 0.5, mt: 0.5}}>
                                                    <Tooltip
                                                        placement="top-end"
                                                        variant="outlined"
                                                        arrow
                                                        title="Запустить повторную обработку задачи">
                                                        <IconButton
                                                            size="sm"
                                                            variant="outlined"
                                                            aria-label="Повторная обработка"
                                                            onClick={() => reschedule(listItem.jobId)}
                                                        >
                                                            <RocketLaunchIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </>}
                                        </div>
                                    </ListItemContent>
                                </ListItem>
                                <ListDivider/>
                            </List>
                        ))}
                        <Box
                            className="Pagination-mobile"
                            sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', py: 2}}
                        >
                            <IconButton
                                aria-label="previous page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page === 1}
                                onClick={() => setPage(state => state - 1)}
                            >
                                <KeyboardArrowLeftIcon/>
                            </IconButton>
                            <Typography level="body-sm" sx={{mx: 'auto'}}>
                                Страница {page} из {pageCount}
                            </Typography>
                            <IconButton
                                aria-label="next page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page === pageCount}
                                onClick={() => setPage(state => state + 1)}
                            >
                                <KeyboardArrowRightIcon/>
                            </IconButton>
                        </Box>
                    </Box>
                </>
            }
        </React.Fragment>
    );
}
